import { usePermissions } from '@/composables/usePermissions'
import { usePostHog } from '@/composables/usePostHog'
import { Permissions } from '@/constants/permissionEnums'
import { authRoutes } from '@/router/auth'
import { PermissionIdentifier } from '@/types/auth'
import { isTierChangeConsoleEnabled } from '@/utils/tierChangeConsole'
import { useTitle } from '@vueuse/core'
import { createRouter, createWebHistory, RouteLocationNormalizedGeneric, RouteRecordRaw } from 'vue-router'

const { posthog, posthogEnabled } = usePostHog()

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Admin',
    component: () => import('@/views/Admin.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: 'merge-jobs',
        name: 'MergeJobs',
        component: () => import('@/views/MergeJobs/MergeJobs.vue'),
        meta: {
          mainRouteName: 'MergeJobs',
          title: 'Merge Jobs | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_MERGE_JOBS],
        },
      },
      {
        path: 'base-products',
        name: 'BaseProducts',
        component: () => import('@/views/BaseProducts/BaseProducts.vue'),
        meta: {
          mainRouteName: 'BaseProducts',
          title: 'Base Products | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_BASE_PRODUCTS],
        },
      },
      {
        path: 'base-products/search',
        name: 'BaseProductsSearch',
        component: () => import('@/views/BaseProducts/BaseProductsSearch.vue'),
        meta: {
          mainRouteName: 'BaseProducts',
          title: 'Base Products Search | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_BASE_PRODUCTS],
        },
      },
      {
        path: 'base-products/:baseProductId/analytics',
        name: 'BaseProductAnalytics',
        component: () => import('@/views/BaseProducts/BaseProductAnalytics.vue'),
        meta: {
          mainRouteName: 'BaseProducts',
          title: 'Base Product Analytics | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_BASE_PRODUCT_ANALYTICS],
        },
      },
      {
        path: 'base-products/merge',
        name: 'MergeBaseProducts',
        component: () => import('@/views/BaseProducts/MergeBaseProducts.vue'),
        meta: {
          mainRouteName: 'BaseProducts',
          title: 'Merge Base Products | PourNow',
          permissionIdentifiers: [Permissions.CAN_MERGE_BASE_PRODUCTS],
        },
      },
      {
        path: 'base-products/new',
        name: 'AddBaseProduct',
        component: () => import('@/views/BaseProducts/AddBaseProduct.vue'),
        meta: {
          mainRouteName: 'BaseProducts',
          title: 'Add New Base Products | PourNow',
          permissionIdentifiers: [Permissions.CAN_CREATE_BASE_PRODUCTS],
        },
      },
      {
        path: 'base-products/:baseProductId',
        name: 'EditBaseProduct',
        component: () => import('@/views/BaseProducts/EditBaseProduct.vue'),
        meta: {
          mainRouteName: 'BaseProducts',
          title: 'Edit Base Products | PourNow',
          permissionIdentifiers: [Permissions.CAN_UPDATE_BASE_PRODUCTS],
        },
      },
      {
        path: 'accounts',
        name: 'Accounts',
        component: () => import('@/views/Accounts/Accounts.vue'),
        meta: {
          mainRouteName: 'Accounts',
          title: 'Accounts | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_ACCOUNT],
        },
      },
      {
        path: 'accounts/:accountId',
        name: 'AccountDetails',
        component: () => import('@/views/Accounts/AccountDetails.vue'),
        meta: {
          mainRouteName: 'Accounts',
          title: 'Account Details | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_ACCOUNT],
        },
      },
      {
        path: 'accounts/:accountId/edit',
        name: 'EditAccount',
        component: () => import('@/views/Accounts/AccountEdit.vue'),
        meta: {
          mainRouteName: 'Accounts',
          title: 'Edit Account | PourNow',
          permissionIdentifiers: [Permissions.CAN_UPDATE_PRE_ACCOUNT, Permissions.CAN_CREATE_PRE_ACCOUNT],
        },
      },
      {
        path: 'brands',
        name: 'Brands',
        component: () => import('@/views/Brands/Brands.vue'),
        meta: {
          mainRouteName: 'Brands',
          title: 'Brands | PourNow',
          permissionIdentifiers: [Permissions.CAN_JUMP_TO_BUSINESS],
        },
      },
      {
        path: 'business/:businessId',
        name: 'BusinessDetails',
        component: () => import('@/views/Business/BusinessDetails.vue'),
        meta: {
          mainRouteName: 'Brands',
          title: 'Business Details | PourNow',
          permissionIdentifiers: [Permissions.CAN_JUMP_TO_BUSINESS],
        },
      },
      {
        path: 'business/:businessId/edit',
        name: 'BusinessEdit',
        component: () => import('@/views/Business/BusinessEdit.vue'),
        meta: {
          mainRouteName: 'Brands',
          title: 'Edit Business | PourNow',
          permissionIdentifiers: [Permissions.CAN_JUMP_TO_BUSINESS],
        },
      },
      {
        path: 'brand-products',
        name: 'BrandProducts',
        component: () => import('@/views/BrandProducts/BrandProducts.vue'),
        meta: {
          mainRouteName: 'BrandProducts',
          title: 'Brand Products | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_ENTITY_PRODUCTS],
        },
      },
      {
        path: 'retailer-products',
        name: 'RetailerProducts',
        component: () => import('@/views/RetailerProducts/RetailerProducts.vue'),
        meta: {
          mainRouteName: 'RetailerProducts',
          title: 'Retailer Products | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_ENTITY_PRODUCTS],
        },
      },
      {
        path: 'brand-products/:entityId/new',
        name: 'AddBrandProduct',
        component: () => import('@/views/EntityProducts/AddProduct.vue'),
        meta: {
          mainRouteName: 'BrandProducts',
          title: 'Add Product | PourNow',
          permissionIdentifiers: [Permissions.CAN_CREATE_ENTITY_PRODUCT],
        },
      },
      {
        path: 'retailer-products/:entityId/new',
        name: 'AddRetailerProduct',
        component: () => import('@/views/EntityProducts/AddProduct.vue'),
        meta: {
          mainRouteName: 'RetailerProducts',
          title: 'Add Product | PourNow',
          permissionIdentifiers: [Permissions.CAN_CREATE_ENTITY_PRODUCT],
        },
      },
      {
        path: 'brand-products/:entityId/new/details',
        name: 'AddBrandProductDetails',
        component: () => import('@/views/EntityProducts/AddProductDetails.vue'),
        meta: {
          mainRouteName: 'BrandProducts',
          title: 'Add New Product | PourNow',
          permissionIdentifiers: [Permissions.CAN_CREATE_ENTITY_PRODUCT],
        },
      },
      {
        path: 'brand-products/:entityId/new/details',
        name: 'AddRetailerProductDetails',
        component: () => import('@/views/EntityProducts/AddProductDetails.vue'),
        meta: {
          mainRouteName: 'RetailerProducts',
          title: 'Add New Product | PourNow',
          permissionIdentifiers: [Permissions.CAN_CREATE_ENTITY_PRODUCT],
        },
      },
      {
        path: 'brand-products/:productId',
        name: 'EditBrandProduct',
        component: () => import('@/views/EntityProducts/EditProduct.vue'),
        meta: {
          mainRouteName: 'BrandProducts',
          title: 'Edit Product | PourNow',
          permissionIdentifiers: [Permissions.CAN_UPDATE_ENTITY_PRODUCT],
        },
      },
      {
        path: 'retailer-products/:productId',
        name: 'EditRetailerProduct',
        component: () => import('@/views/EntityProducts/EditProduct.vue'),
        meta: {
          mainRouteName: 'RetailerProducts',
          title: 'Edit Product | PourNow',
          permissionIdentifiers: [Permissions.CAN_UPDATE_ENTITY_PRODUCT],
        },
      },
      {
        path: 'brand-products/add-variant/:productId',
        name: 'AddVariant',
        component: () => import('@/views/EntityProducts/AddVariant.vue'),
        meta: {
          mainRouteName: 'BrandProducts',
          title: 'Add Variant | PourNow',
          permissionIdentifiers: [Permissions.CAN_ADD_VARIANT_TO_ENTITY_PRODUCT],
        },
      },
      {
        path: 'matching-engine',
        name: 'MatchingEngine',
        component: () => import('@/views/MatchingEngine/MatchingEngine.vue'),
        meta: {
          mainRouteName: 'MatchingEngine',
          title: 'Matching Engine | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_MATCHING_ENGINE],
        },
      },
      {
        path: 'match-product/:itemId',
        name: 'MatchProduct',
        component: () => import('@/views/MatchingEngine/MatchProduct.vue'),
        meta: {
          mainRouteName: 'MatchingEngine',
          title: 'Match Product | PourNow',
          permissionIdentifiers: [Permissions.CAN_MATCH_ENTITY_PRODUCT_TO_BASE_BOTTLE],
        },
      },
      {
        path: 'pre-accounts',
        name: 'PreAccounts',
        component: () => import('@/views/PreAccounts/PreAccounts.vue'),
        meta: {
          mainRouteName: 'PreAccounts',
          title: 'Pre-Accounts | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_PRE_ACCOUNTS, Permissions.CAN_SEARCH_PRE_ACCOUNT],
        },
      },
      {
        path: 'pre-accounts/:preAccountId',
        name: 'AddEditPreAccount',
        component: () => import('@/views/PreAccounts/AddEditPreAccount.vue'),
        meta: {
          mainRouteName: 'PreAccounts',
          title: 'Add Pre-Account | PourNow',
          permissionIdentifiers: [Permissions.CAN_CREATE_PRE_ACCOUNT, Permissions.CAN_UPDATE_PRE_ACCOUNT],
        },
      },
      {
        path: 'retailer-check',
        name: 'RetailerCheck',
        component: () => import('@/views/RetailerCheck/RetailerCheck.vue'),
        meta: {
          mainRouteName: 'RetailerCheck',
          title: 'Retailer Check | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_ACCOUNT],
        },
      },
      {
        path: 'billing-reconciliation',
        name: 'BillingReconciliationReview',
        component: () => import('@/views/BillingReconciliation/BillingReconciliationReview.vue'),
        // No route-level host gate: the reconciliation board is the #81/#267
        // read-only review view and stays VISIBLE on the production host. The
        // money-moving execute (repair) action inside the board is gated to
        // non-prod hosts via isTierChangeConsoleEnabled() (see the component's
        // repairsEnabled). GET rows works on all hosts.
        meta: {
          mainRouteName: 'BillingReconciliationReview',
          title: 'Billing Reconciliation Review | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_ACCOUNT],
        },
      },
      {
        path: 'tier-change-console',
        name: 'TierChangeConsole',
        component: () => import('@/views/BillingReconciliation/TierChangeConsole.vue'),
        beforeEnter: () => (isTierChangeConsoleEnabled() ? true : { name: 'Admin' }),
        meta: {
          mainRouteName: 'TierChangeConsole',
          title: 'Tier Change Console | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_ACCOUNT],
        },
      },
      {
        path: 'staging-sync',
        name: 'StagingSyncConsole',
        component: () => import('@/views/BillingReconciliation/StagingSyncConsole.vue'),
        // Fail-closed host gate: this copies REAL prod content into the local
        // non-prod DB, so it is disabled on the production host. Same non-prod
        // allowlist as the Tier Change Console; the backend also refuses on prod.
        beforeEnter: () => (isTierChangeConsoleEnabled() ? true : { name: 'Admin' }),
        meta: {
          mainRouteName: 'StagingSyncConsole',
          title: 'Sync Staging from Prod | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_ACCOUNT],
        },
      },
      {
        path: 'account-setup-provision',
        name: 'AccountSetupProvisionConsole',
        component: () => import('@/views/BillingReconciliation/AccountSetupProvisionConsole.vue'),
        // Same fail-closed host gate as the Tier Change Console (#347/#222): drives
        // real Stripe writes, so disabled on the production host until go-live.
        beforeEnter: () => (isTierChangeConsoleEnabled() ? true : { name: 'Admin' }),
        meta: {
          mainRouteName: 'AccountSetupProvisionConsole',
          title: 'Account Setup & Provision | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_ACCOUNT],
        },
      },
      {
        path: 'reported-issues',
        name: 'ReportedIssues',
        component: () => import('@/views/ReportedIssues/ReportedIssues.vue'),
        meta: {
          mainRouteName: 'ReportedIssues',
          title: 'Reported Issues | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_REPORTED_ISSUES],
        },
      },
      {
        path: 'barcode-resolution',
        name: 'BarcodeResolution',
        component: () => import('@/views/BarcodeResolution/BarcodeResolution.vue'),
        meta: {
          mainRouteName: 'BarcodeResolution',
          title: 'Barcode Resolution | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_BARCODE_RESOLUTION],
        },
      },
      {
        path: 'barcode-resolution/:id',
        name: 'BarcodeResolutionDetail',
        component: () => import('@/views/BarcodeResolution/BarcodeResolutionDetail.vue'),
        meta: {
          mainRouteName: 'BarcodeResolution',
          title: 'Barcode Resolution | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_BARCODE_RESOLUTION],
        },
      },
      {
        path: 'blacklisted-products',
        name: 'BlacklistedProducts',
        component: () => import('@/views/BlacklistedProducts/BlacklistedProducts.vue'),
        meta: {
          mainRouteName: 'BlacklistedProducts',
          title: 'Blacklisted Products | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_BLACKLISTED_PRODUCTS],
        },
      },
      {
        path: 'pod-files',
        name: 'PODFiles',
        component: () => import('@/views/PODFiles/PODFiles.vue'),
        meta: {
          mainRouteName: 'PODFiles',
          title: 'POD Files | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_POD_FILES],
        },
      },
      {
        path: 'create-new-business',
        name: 'CreateNewBusiness',
        component: () => import('@/views/CreateNewBusiness/CreateNewBusiness.vue'),
        meta: {
          mainRouteName: 'CreateNewBusiness',
          title: 'Create New Business | PourNow',
        },
      },
      {
        path: 'overview',
        name: 'Overview',
        component: () => import('@/views/Overview/Overview.vue'),
        meta: {
          mainRouteName: 'Overview',
          title: 'Overview | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_OVERVIEW_ANALYTICS],
        },
      },
      {
        path: 'retailers/:regionId/:regionCode',
        name: 'RetailersByRegion',
        component: () => import('@/views/Overview/RetailersByRegion.vue'),
        meta: {
          mainRouteName: 'Overview',
          title: 'Retailers By Region | PourNow',
          permissionIdentifiers: [Permissions.CAN_VIEW_OVERVIEW_ANALYTICS],
        },
      },
    ],
  },
  {
    path: '/no-access',
    name: 'NoAccess',
    component: () => import('@/views/Error/NoAccess.vue'),
    meta: {
      requiresAuth: true,
      title: 'No Access | PourNow',
    },
  },
  ...authRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/Error/NotFound.vue'),
    meta: {
      title: 'Not Found | PourNow',
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    window.location.href = to.fullPath
  }
})

router.beforeEach((to, from) => {
  if (posthogEnabled) posthog.capture('$pageleave')

  const title = useTitle()
  const authStore = useAuthStore()
  const permissions = usePermissions()

  if (typeof to.meta.title === 'string') {
    title.value = to.meta.title
  }

  if (to.name === 'NotFound') return true

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const isAuthenticated = Boolean(authStore.token)

  if (requiresAuth && !isAuthenticated) return { name: 'Login' }
  if (!requiresAuth && isAuthenticated) return { name: 'Admin' }

  if (to.name === 'Admin') {
    return handleAdminRedirect()
  }

  return handlePermissionCheck(to, from, permissions)
})

router.afterEach(() => {
  if (posthogEnabled) posthog.capture('$pageview')
})

const handleAdminRedirect = () => {
  const permissions = usePermissions()

  const adminRoute = routes.find((route) => route.name === 'Admin')
  const mainRoutes = adminRoute?.children?.filter((route) => route.name === route.meta?.mainRouteName) || []

  const accessibleRoutes = mainRoutes.filter((route) => {
    const routePermissions = (route.meta?.permissionIdentifiers as PermissionIdentifier[]) || []
    return routePermissions.some((id) => permissions.hasPermission(id))
  })

  if (accessibleRoutes.some((route) => route.name === 'MatchingEngine')) {
    return { name: 'MatchingEngine' }
  }

  return accessibleRoutes.length ? { name: accessibleRoutes[0].name } : { name: 'NoAccess' }
}

const handlePermissionCheck = (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
  permissions: ReturnType<typeof usePermissions>,
): { name: string } | true => {
  const requiredPermissions = (to.meta.permissionIdentifiers as PermissionIdentifier[]) || []
  const hasPermission =
    requiredPermissions.length === 0 || requiredPermissions.some((id) => permissions.hasPermission(id))

  return hasPermission ? true : { name: (from.name as string) || 'NotFound' }
}

export default router
